import { notification } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  InfoCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons';

// Per-type design tokens. Values mirror antd's <Alert type=…> defaults
// so the toaster visually matches the static <Alert> banner rendered
// at the top of the layout (see src/components/Feedback/Alert.style.js +
// containers/Topbar/Topbar.js) — operators see the same colour language
// whether the message is inline or in the toast drawer.
const TOKENS = {
  success: { bg: '#f6ffed', border: '#b7eb8f', icon: '#52c41a', Icon: CheckCircleFilled },
  error:   { bg: '#fff2f0', border: '#ffccc7', icon: '#ff4d4f', Icon: CloseCircleFilled },
  info:    { bg: '#e6f7ff', border: '#91d5ff', icon: '#1890ff', Icon: InfoCircleFilled },
  warning: { bg: '#fffbe6', border: '#ffe58f', icon: '#faad14', Icon: ExclamationCircleFilled },
};

const DURATIONS = { success: 4, info: 4, warning: 5, error: 6 };

// Top-right toaster that renders the same visual language as the static
// <Alert type=… showIcon closable> banner. One code path for every
// transient notification in the app so the design (tint, icon, padding,
// width, close button) can't drift between modules. Errors stay on
// screen longer because users often need to read them before reacting.
//
// Two call shapes:
//   notify('success', 'Re-notify window set to 4 hours.')
//     → single-line card.
//   notify('error', 'Save failed', 'The reminder window could not be saved. Please try again.')
//     → two-line card (bold title, regular description underneath).
//
// Both forms use the same tinted card; the optional third argument adds
// a description line under the title. Use it when the title alone is
// too terse to be actionable (typical for permission errors, remediation
// results, validation failures with next-steps).
const notify = (type, message, description) => {
  const t = TOKENS[type] || TOKENS.info;
  const { Icon } = t;
  notification.open({
    message,
    description,
    icon: <Icon style={{ color: t.icon, fontSize: 22 }} />,
    placement: 'topRight',
    duration: DURATIONS[type] || 4,
    // Keep the close button visible — matches the static <Alert closable>
    // banner that operators are already used to.
    closeIcon: undefined,
    className: `app-toast app-toast--${type}`,
    style: {
      width: 'auto',
      minWidth: 320,
      maxWidth: 520,
      padding: '16px 18px',
      borderRadius: 10,
      background: t.bg,
      border: `1px solid ${t.border}`,
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06)',
    },
  });
};

export default notify;
